import { FC, useState } from 'react';
import styles from './styles.module.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons';
import { addDays, format } from 'date-fns';
import { useGetUnallocatedQuery } from '../../../../api/carAllocationApi';
import { UnallocatedTask } from '../../../../types/carAllocation';
import classNames from 'classnames';
import DetailPopup from './DetailPopup';
import WeekControl from './WeekControl';
import { getCalcStatusText } from '../../../../services/getCalcStatusText';
import { ja } from 'date-fns/locale';
import { formatTime } from '../../../../services/formatTime';

type Props = {
  start: string;
  onClose: () => void;
};
const dateQueryFormat = 'yyyy-MM-dd';
const UnallocatedList: FC<Props> = ({ start, onClose }) => {
  const [startDate, setStartDate] = useState(new Date(start));
  const endDate = addDays(startDate, 6);
  const [detailSelected, setDetailSelected] = useState<UnallocatedTask | null>(
    null
  );
  const onCloseDetailPopup = () => setDetailSelected(null);

  const { data } = useGetUnallocatedQuery({
    startDate: format(startDate, dateQueryFormat),
    endDate: format(endDate, dateQueryFormat),
  });

  return (
    <div className={styles.container}>
      {detailSelected && (
        <div className={styles.popupContainer}>
          <DetailPopup selected={detailSelected} onClose={onCloseDetailPopup} />
        </div>
      )}
      <div className={styles.scrollContainer}>
        <div className={styles.head}>
          <span className={styles.headText}>未配車リスト</span>
          <button className={styles.button} onClick={onClose}>
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>
        <WeekControl
          start={startDate}
          onStartDateChange={setStartDate}
          count={data?.total || 0}
        />
        <div className={styles.dataCellContainer}>
          {data?.unallocatedCarAllocations.map((task) => (
            <DataCell
              key={task.contractId}
              selected={
                !!detailSelected &&
                detailSelected.contractId === task.contractId
              }
              task={task}
              onDetailed={setDetailSelected}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

type DataCellProp = {
  task: UnallocatedTask;
  selected: boolean;
  onDetailed: (task: UnallocatedTask) => void;
};
const DataCell: FC<DataCellProp> = ({ task, selected, onDetailed }) => {
  const notAllocated = new Date(task.notAllocatedDay);
  const dateStr = format(notAllocated, 'M/d', { locale: ja });
  const weekDay = format(notAllocated, 'E', { locale: ja });
  const specificTimeText = task.specificTimeStartAt
    ? task.specificTimeEndAt
      ? `${formatTime(task.specificTimeStartAt)}-${formatTime(
          task.specificTimeEndAt
        )}`
      : `${formatTime(task.specificTimeStartAt)}`
    : '';
  return (
    <div
      className={classNames(styles.dataCell, selected && styles.selected)}
      onClick={() => onDetailed(task)}
    >
      <div className={styles.dataCellHeadContainer}>
        <div className={styles.accentBar}></div>
        <div className={styles.dataCellHead}>
          <div className={styles.dateContainer}>
            <div className={styles.date}>{dateStr}</div>
            <div className={styles.weekDay}>{`(${weekDay})`}</div>
          </div>
          <div className={styles.status}>
            {getCalcStatusText(task.calcStatus)}
          </div>
        </div>
      </div>
      <div className={styles.data}>
        <div className={styles.dataTitle}>{task.company.name}</div>
        <div className={styles.dataBody}>
          {specificTimeText && (
            <div className={styles.dataTime}>
              {specificTimeText}
              <div className={styles.constraintLabel}>指定</div>
            </div>
          )}
          {task.hasCarRestriction && (
            <div className={styles.carConstraintLabel}>※車両制限あり</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UnallocatedList;
